import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";

/**
 * QR modal for the unified multi-brand /order route.
 * One generic QR that links to ${baseUrl}/order — no per-table variants.
 */
export const MultiBrandQRModal = ({ baseUrl, onClose }) => {
    const canvasWrapRef = useRef(null);
    const url = `${baseUrl}/order`;

    const downloadPng = () => {
        const canvas = canvasWrapRef.current?.querySelector("canvas");
        if (!canvas) return;
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/png");
        a.download = `naami-qr-multi-brand.png`;
        a.click();
    };

    const printQR = () => {
        const canvas = canvasWrapRef.current?.querySelector("canvas");
        if (!canvas) return;
        const dataUrl = canvas.toDataURL("image/png");
        const w = window.open("", "_blank");
        if (!w) {
            alert("Pop-up blocked. Allow pop-ups for this site to print the QR.");
            return;
        }
        w.document.write(`<!doctype html><html><head><title>QR · Multi-brand</title>
            <style>
                @page { size: 100mm 150mm; margin: 0; }
                body { margin: 0; font-family: -apple-system, system-ui, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                .card { width: 100mm; height: 150mm; padding: 8mm; box-sizing: border-box; display: flex; flex-direction: column; align-items: center; text-align: center; border: 1px dashed #aaa; }
                .eyebrow { font-size: 9pt; letter-spacing: 0.22em; text-transform: uppercase; color: #C8A65A; margin: 0 0 4mm 0; font-weight: 700; }
                h1 { font-size: 22pt; margin: 0 0 1mm 0; line-height: 1.05; color: #111; }
                .tag { font-size: 9pt; color: #666; margin: 0 0 5mm 0; }
                img { width: 70mm; height: 70mm; image-rendering: pixelated; margin: 2mm 0; }
                .cta { font-size: 12pt; font-weight: 700; margin: 4mm 0 1mm 0; color: #111; }
                .hint { font-size: 9pt; color: #888; }
                .url { font-family: monospace; font-size: 8pt; color: #aaa; margin-top: auto; word-break: break-all; }
                @media print { .card { border: none; } body { background: white; } }
            </style></head><body>
            <div class="card">
                <p class="eyebrow">// Naami Kitchens</p>
                <h1>Order from any brand</h1>
                <p class="tag">14 kitchens · one basket · collection at counter</p>
                <img src="${dataUrl}" alt="QR" />
                <p class="cta">Scan to order →</p>
                <p class="hint">Open camera · point at QR · tap link</p>
                <p class="url">${url}</p>
            </div>
            <script>window.onload = () => { window.focus(); window.print(); };</script>
            </body></html>`);
        w.document.close();
    };

    const copyUrl = async () => {
        try {
            await navigator.clipboard.writeText(url);
            alert("URL copied to clipboard");
        } catch {
            const ta = document.createElement("textarea");
            ta.value = url;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            ta.remove();
            alert("URL copied to clipboard");
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-[var(--bg)]/95 backdrop-blur grid place-items-center p-6"
            onClick={onClose}
            data-testid="multi-qr-modal-backdrop"
        >
            <div
                className="bg-[var(--surface)] border border-[var(--border)] max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <p className="eyebrow">// Multi-brand QR</p>
                    <button
                        onClick={onClose}
                        className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hover:text-gold"
                        data-testid="multi-qr-modal-close"
                    >
                        ✕ Close
                    </button>
                </div>

                <h3 className="font-display text-[26px] mb-1 text-gold">
                    Order from any brand
                </h3>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mb-5">
                    14 kitchens · one basket · collection only
                </p>

                <div
                    ref={canvasWrapRef}
                    className="bg-white p-4 grid place-items-center mb-4 mx-auto"
                    style={{ width: 240, height: 240 }}
                    data-testid="multi-qr-canvas-wrap"
                >
                    <QRCodeCanvas
                        value={url}
                        size={208}
                        level="H"
                        marginSize={0}
                        bgColor="#ffffff"
                        fgColor="#0a0a0a"
                    />
                </div>

                <div className="bg-[var(--surface-elevated)] border border-[var(--border)] px-3 py-2 font-mono text-[11px] text-muted mb-5 break-all" data-testid="multi-qr-url-display">
                    {url}
                </div>

                <div className="grid grid-cols-3 gap-2">
                    <button onClick={downloadPng} className="btn-primary justify-center" data-testid="multi-qr-download-btn">
                        ⬇ PNG
                    </button>
                    <button onClick={printQR} className="btn-secondary justify-center" data-testid="multi-qr-print-btn">
                        🖨 Print
                    </button>
                    <button onClick={copyUrl} className="btn-secondary justify-center" data-testid="multi-qr-copy-btn">
                        ⧉ Copy
                    </button>
                </div>

                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted text-center mt-5">
                    Tip: Print at 100mm × 150mm for a table-tent
                </p>
            </div>
        </div>
    );
};

export default MultiBrandQRModal;
